import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { OrgApiError, OrgApiService, OrgMine, OrgMineEntry, OrgOverview, OrgUnit } from './org-api.service';

/**
 * Which organisation the org screens are looking at, and which units that means.
 *
 * The unit set comes from the server — `GET /org/:id/units` — and never from the
 * browser. That is the contract's rule and the whole security model: the caller
 * may narrow a unit set, never widen it, so the server decides what "this
 * organisation" is. A multi-entity group therefore spans every unit under the
 * OrganisationId regardless of which ones this user is individually assigned to,
 * which is exactly the point of an org admin.
 *
 * `support` (PubQ staff, custom claim `pubqSupport`) may open any organisation
 * even though `/org/mine` lists only explicit grants.
 */

export interface OrgSelection {
    organisation: OrgMineEntry | null;
    overview: OrgOverview | null;
    units: OrgUnit[];
    /** Set when the selection could not be loaded, for the screen to show. */
    error?: string;
}

const STORAGE_KEY = 'PUBQ_ORG_SELECTED';

@Injectable({ providedIn: 'root' })
export class OrgContextService {
    private readonly selection$ = new BehaviorSubject<OrgSelection>({ organisation: null, overview: null, units: [] });
    readonly selected$ = this.selection$.asObservable();

    organisations: OrgMineEntry[] = [];
    support = false;
    /** Set once `/org/mine` has answered, so the menu and guard stop guessing. */
    loaded = false;
    loadError: string | null = null;

    private loading: Promise<void> | null = null;

    constructor(private api: OrgApiService) {}

    get selection(): OrgSelection {
        return this.selection$.value;
    }

    /** An org admin of this organisation, or PubQ staff. */
    isOrgAdmin(organisationId?: number | null): boolean {
        if (this.support) return true;
        const id = organisationId ?? this.selection.organisation?.organisationId ?? null;
        return id !== null && this.organisations.some((o) => o.organisationId === id);
    }

    /** Does this user get the org area at all? */
    get hasOrgAccess(): boolean {
        return this.support || this.organisations.length > 0;
    }

    /**
     * Load `/org/mine` and select an organisation. Shared between the guard, the
     * menu and the four screens, so it runs once per page load rather than five
     * times.
     */
    load(): Promise<void> {
        if (this.loading) return this.loading;
        this.loading = (async () => {
            try {
                const mine: OrgMine = await this.api.mine();
                this.support = !!mine.support;
                this.organisations = mine.organisations ?? [];
                this.loadError = null;
            } catch (err) {
                this.support = false;
                this.organisations = [];
                this.loadError = err instanceof OrgApiError
                    ? (err.missing ? 'The organisation API is not deployed in this environment.' : err.message)
                    : 'The organisation API could not be reached.';
            } finally {
                this.loaded = true;
            }

            const remembered = this.remembered();
            const pick = this.organisations.find((o) => o.organisationId === remembered) ?? this.organisations[0] ?? null;
            if (pick) await this.select(pick.organisationId);
        })();
        try {
            return this.loading;
        } finally {
            // Let a later call reload rather than replay a stale promise.
            this.loading.finally(() => { this.loading = null; });
        }
    }

    async select(organisationId: number): Promise<void> {
        const organisation = this.organisations.find((o) => o.organisationId === organisationId)
            // Staff may open an org they hold no grant for; synthesise the entry.
            ?? (this.support ? { organisationId, name: `Organisation ${organisationId}`, orgRoute: null, grantedAtUtc: null, unitCount: 0 } : null);
        if (!organisation) {
            this.selection$.next({ organisation: null, overview: null, units: [] });
            return;
        }
        this.remember(organisationId);
        try {
            const [overview, units] = await Promise.all([
                this.api.overview(organisationId),
                this.api.units(organisationId),
            ]);
            this.selection$.next({
                organisation: { ...organisation, name: overview.name || organisation.name, unitCount: overview.unitCount ?? organisation.unitCount },
                overview,
                units: units.units ?? [],
            });
        } catch (err) {
            this.selection$.next({
                organisation,
                overview: null,
                units: [],
                error: err instanceof OrgApiError ? err.message : 'The organisation could not be loaded.',
            });
        }
    }

    private remembered(): number | null {
        try {
            const v = localStorage.getItem(STORAGE_KEY);
            return v === null ? null : Number(v);
        } catch {
            return null;
        }
    }

    private remember(id: number): void {
        try {
            localStorage.setItem(STORAGE_KEY, String(id));
        } catch {
            /* a private window just loses the preference */
        }
    }
}
